import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { ProductSelectionTable } from 'components/Quote/ProductSelectionTable';
import { useCalc } from 'hooks/useCalc';
import { toast } from 'material-react-toastify';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LookupSelect } from 'shared/components/LookupSelect';
import { calcOutputToDiscountModelResult } from 'shared/helpers';
import { Customer } from 'shared/types/customer';
import { Product } from 'shared/types/product';
import { QuoteLine } from 'shared/types/quoteline';

const dialogTitleStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
};

// MUI 5 currently has a bug which cuts the necessary padding off of the top of the content in a dialog.
// This overrides that bug to force the legacy padding into the dialog content.
// https://github.com/mui-org/material-ui/issues/26795
const dialogContentStyles: SxProps<Theme> = {
  padding: '8px 24px !important',
};

const dialogPaperStyles: SxProps<Theme> = {
  height: '80vh',
};

const actionsContainerStyles: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '25px 25px',
  borderTop: '1px solid grey',
};

interface AddProductDialogProps {
  customer: Customer;
  open: boolean;
  onClose: (event: any, reason: string) => void;
  quoteLines?: QuoteLine[];
  handleSetQuoteLines: (calcOutputs: QuoteLine[]) => void;
}

export const AddProductDialog = ({
  open,
  onClose,
  customer,
  quoteLines,
  handleSetQuoteLines,
}: AddProductDialogProps) => {
  const [tempQuoteLines, setTempQuoteLines] = useState<QuoteLine[]>(quoteLines ?? []);
  const [loading, setLoading] = useState(false);
  const { performCalc } = useCalc();

  useEffect(() => {
    if (quoteLines) {
      setTempQuoteLines(quoteLines);
    }
  }, [quoteLines, setTempQuoteLines]);

  const onChangeLine = useCallback(
    (lineNumber: number, newQuantity: number) => {
      const tempArr = tempQuoteLines;
      tempArr[lineNumber] = { ...tempArr[lineNumber], quantity: newQuantity };
      setTempQuoteLines([...tempArr]);
    },
    [setTempQuoteLines, tempQuoteLines]
  );

  const onDeleteLine = useCallback(
    (lineNumber: number) => {
      const tempArr = tempQuoteLines;
      tempArr.splice(lineNumber, 1);
      setTempQuoteLines([...tempArr.map((l, index) => ({ ...l, lineNumber: index }))]);
    },
    [tempQuoteLines, setTempQuoteLines]
  );

  const onAddLine = useCallback(
    (e: React.ChangeEvent<{}>, product: string | Product | null) => {
      if (!product || typeof product === 'string') return;
      setTempQuoteLines(state => [
        { product, quantity: 0, lineNumber: 0 },
        ...state.map(x => ({ ...x, lineNumber: x.lineNumber + 1 })), // keep the line numbers matching the indices
      ]);
    },
    [setTempQuoteLines]
  );

  const calcInputs = useMemo(() => {
    return [
      {
        name: 'CustomerNumber',
        value: customer.uniqueid,
        returnInOutput: true,
        type: 'string',
      },
      {
        name: 'QuoteLines',
        value: tempQuoteLines.map(l => [
          {
            name: 'ItemNumber',
            value: l.product.uniqueid,
            returnInOutput: true,
            type: 'string',
          },
          {
            name: 'Quantity',
            value: l.quantity,
            returnInOutput: true,
            type: 'number',
          },
          {
            name: 'LineNumber',
            value: l.lineNumber,
            returnInOutput: true,
            type: 'number',
          },
        ]),
        returnInOutput: false,
        type: 'array',
      },
    ];
  }, [tempQuoteLines, customer]);

  const getPricing = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      performCalc({
        clientId: 'demo-plq',
        modelId: 'parentModelAnonTS',
        inputs: calcInputs,
      })
        .then(res => {
          if (!res) return;
          const formattedResults = calcOutputToDiscountModelResult(res);
          const newQuoteLines: QuoteLine[] = tempQuoteLines.map(l => ({
            ...l,
            calcResults: formattedResults.find(x => x.LineNumber === l.lineNumber),
          }));
          handleSetQuoteLines(newQuoteLines);
          toast.success('Pricing calculated successfully!');
          onClose(null, 'finishedCalculation');
        })
        .catch(error => {
          toast.error('Unable to calculate pricing. Please try again or start over.');
        })
        .finally(() => {
          setLoading(false);
        });
    }, 1000);
  }, [performCalc, calcInputs, handleSetQuoteLines, tempQuoteLines, onClose]);

  return (
    <Dialog PaperProps={{ sx: dialogPaperStyles }} open={open} onClose={onClose} fullWidth={true} maxWidth='lg'>
      <DialogTitle sx={dialogTitleStyles}>
        <Typography variant='h6' display='inline'>
          Select Products
        </Typography>
      </DialogTitle>
      <DialogContent sx={dialogContentStyles}>
        <div style={{ maxWidth: 450 }}>
          <LookupSelect<Product>
            clientId='demo-plq'
            columnName='item_number'
            dataType='string'
            getOptionLabel={opt => opt.item_number}
            handleSelect={onAddLine}
            inputCasing='UPPERCASE'
            queryOperator='contains'
            tableId='itemmasteranonts'
            title='Search products by Item Number...'
          />
        </div>
        <ProductSelectionTable lines={tempQuoteLines} onChangeLine={onChangeLine} onDeleteLine={onDeleteLine} />
      </DialogContent>
      <DialogActions sx={actionsContainerStyles}>
        <Button color='secondary' variant='outlined' onClick={() => onClose(null, 'clickCancel')} disabled={loading}>
          Cancel
        </Button>
        <Button color='primary' variant='contained' onClick={getPricing} disabled={loading}>
          {loading ? 'Please Wait...' : 'Calculate Pricing'}
        </Button>
      </DialogActions>
      {loading && <LinearProgress color='secondary' />}
    </Dialog>
  );
};
